import { useState, useMemo } from 'react'
import {
  Crown,
  Plus,
  Copy,
  Archive,
  Trash2,
  CheckCircle2,
  Clock,
  Sparkles,
  Zap,
  Edit2,
  DollarSign,
  Layers,
  Settings2,
  Filter,
  Check,
  Building2,
  Store,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, Input } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import {
  useAdminSubscriptions,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDuplicatePlanMutation,
  useArchivePlanMutation,
  useDeletePlanMutation,
} from '../../hooks/use-admin'

export function PlansManagementPage() {
  const { data: subscriptionsData, isLoading } = useAdminSubscriptions()
  const rawPlans = subscriptionsData?.plans ?? []

  const createPlanMutation = useCreatePlanMutation()
  const updatePlanMutation = useUpdatePlanMutation()
  const duplicatePlanMutation = useDuplicatePlanMutation()
  const archivePlanMutation = useArchivePlanMutation()
  const deletePlanMutation = useDeletePlanMutation()

  const [activeFilter, setActiveFilter] = useState<'ALL' | 'SELLER' | 'DEALER' | 'CUSTOMER' | 'ARCHIVED'>('ALL')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<any | null>(null)

  // Filtered Plans list
  const filteredPlans = useMemo(() => {
    if (activeFilter === 'ARCHIVED') {
      return rawPlans.filter((p: any) => p.isArchived)
    }
    const nonArchived = rawPlans.filter((p: any) => !p.isArchived)
    if (activeFilter === 'ALL') return nonArchived
    return nonArchived.filter((p: any) => p.planType === activeFilter)
  }, [rawPlans, activeFilter])

  // Plan Form State
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [planType, setPlanType] = useState<'SELLER' | 'DEALER' | 'CUSTOMER'>('SELLER')
  const [price, setPrice] = useState(0)
  const [monthlyPrice, setMonthlyPrice] = useState(0)
  const [quarterlyPrice, setQuarterlyPrice] = useState(0)
  const [yearlyPrice, setYearlyPrice] = useState(0)
  const [billingCycle, setBillingCycle] = useState<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY')
  const [trialDays, setTrialDays] = useState(0)
  const [isPopular, setIsPopular] = useState(false)
  const [color, setColor] = useState('#3B82F6')
  const [razorpayProductId, setRazorpayProductId] = useState('')
  const [razorpayPlanId, setRazorpayPlanId] = useState('')
  const [gracePeriodDays, setGracePeriodDays] = useState(3)

  // Extended Feature Matrix State
  const [maxActiveListings, setMaxActiveListings] = useState('10')
  const [maxImagesPerListing, setMaxImagesPerListing] = useState('15')
  const [maxVideosPerListing, setMaxVideosPerListing] = useState('2')
  const [maxBranches, setMaxBranches] = useState('1')
  const [maxStaffAccounts, setMaxStaffAccounts] = useState('2')
  const [leadCredits, setLeadCredits] = useState('25')
  const [boostCredits, setBoostCredits] = useState('50')
  const [analytics, setAnalytics] = useState('true')
  const [prioritySupport, setPrioritySupport] = useState('false')

  const handleOpenCreate = () => {
    setEditingPlan(null)
    setName('')
    setSlug('')
    setDescription('')
    setPlanType('SELLER')
    setPrice(999)
    setMonthlyPrice(999)
    setQuarterlyPrice(2699)
    setYearlyPrice(9999)
    setBillingCycle('MONTHLY')
    setTrialDays(14)
    setIsPopular(false)
    setColor('#3B82F6')
    setRazorpayProductId('')
    setRazorpayPlanId('')
    setGracePeriodDays(3)
    setIsModalOpen(true)
  }

  const handleOpenEdit = (plan: any) => {
    setEditingPlan(plan)
    setName(plan.name)
    setSlug(plan.slug)
    setDescription(plan.description ?? '')
    setPlanType(plan.planType ?? 'SELLER')
    setPrice(Number(plan.price))
    setMonthlyPrice(plan.monthlyPrice ? Number(plan.monthlyPrice) : Number(plan.price))
    setQuarterlyPrice(plan.quarterlyPrice ? Number(plan.quarterlyPrice) : Number(plan.price) * 3)
    setYearlyPrice(plan.yearlyPrice ? Number(plan.yearlyPrice) : Number(plan.price) * 10)
    setBillingCycle(plan.billingCycle ?? 'MONTHLY')
    setTrialDays(plan.trialDays ?? 0)
    setIsPopular(plan.isPopular ?? false)
    setColor(plan.color ?? '#3B82F6')
    setRazorpayProductId(plan.razorpayProductId ?? '')
    setRazorpayPlanId(plan.razorpayPlanId ?? '')
    setGracePeriodDays(plan.gracePeriodDays ?? 3)
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const features = [
      { featureKey: 'MAX_ACTIVE_LISTINGS', featureValue: maxActiveListings },
      { featureKey: 'MAX_IMAGES_PER_LISTING', featureValue: maxImagesPerListing },
      { featureKey: 'MAX_VIDEOS_PER_LISTING', featureValue: maxVideosPerListing },
      { featureKey: 'MAX_BRANCHES', featureValue: maxBranches },
      { featureKey: 'MAX_STAFF_ACCOUNTS', featureValue: maxStaffAccounts },
      { featureKey: 'LEAD_CREDITS', featureValue: leadCredits },
      { featureKey: 'BOOST_CREDITS', featureValue: boostCredits },
      { featureKey: 'ANALYTICS', featureValue: analytics },
      { featureKey: 'PRIORITY_SUPPORT', featureValue: prioritySupport },
    ]

    const payload = {
      name,
      slug: slug || name.toLowerCase().replace(/\s+/g, '-'),
      description,
      planType,
      price,
      monthlyPrice,
      quarterlyPrice,
      yearlyPrice,
      billingCycle,
      trialDays,
      isPopular,
      color,
      razorpayProductId,
      razorpayPlanId,
      gracePeriodDays,
      features,
    }

    if (editingPlan) {
      await updatePlanMutation.mutateAsync({ id: editingPlan.id, payload })
    } else {
      await createPlanMutation.mutateAsync(payload)
    }

    setIsModalOpen(false)
  }

  const getFeatureValue = (features: any[], key: string, fallback: string) => {
    const feat = (features || []).find((f) => f.featureKey === key)
    return feat ? feat.featureValue : fallback
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <SectionHeading
          eyebrow="Platform Subscriptions"
          title="Subscription Plan Control Center"
          description="Configure merchant tier plans, extended feature limits, trial periods, and Razorpay product mapping."
          align="left"
        />

        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {/* Tier Filter Pills */}
          <div className="flex rounded-xl bg-card border border-border/80 p-1 shadow-elevation-xs gap-1 text-xs">
            <button
              type="button"
              onClick={() => setActiveFilter('ALL')}
              className={`rounded-lg px-3 py-1.5 font-extrabold transition-all ${
                activeFilter === 'ALL'
                  ? 'bg-primary text-primary-foreground shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              All Plans ({rawPlans.filter((p) => !p.isArchived).length})
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('SELLER')}
              className={`rounded-lg px-3 py-1.5 font-extrabold transition-all ${
                activeFilter === 'SELLER'
                  ? 'bg-primary text-primary-foreground shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Seller Tier
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('DEALER')}
              className={`rounded-lg px-3 py-1.5 font-extrabold transition-all ${
                activeFilter === 'DEALER'
                  ? 'bg-purple-600 text-white shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Dealer Tier
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter('ARCHIVED')}
              className={`rounded-lg px-3 py-1.5 font-extrabold transition-all ${
                activeFilter === 'ARCHIVED'
                  ? 'bg-amber-600 text-white shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Archived ({rawPlans.filter((p) => p.isArchived).length})
            </button>
          </div>

          <Button onClick={handleOpenCreate} className="gap-2 font-bold bg-primary shadow-glow shrink-0">
            <Plus className="size-4" /> Create New Plan
          </Button>
        </div>
      </div>

      {/* Plans List Cards */}
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading subscription plans...</p>
      ) : filteredPlans.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredPlans.map((plan) => {
            const activeListings = getFeatureValue(plan.features, 'MAX_ACTIVE_LISTINGS', '10')
            const leadCreditsVal = getFeatureValue(plan.features, 'LEAD_CREDITS', '25')
            const boostCreditsVal = getFeatureValue(plan.features, 'BOOST_CREDITS', '50')

            return (
              <Card
                key={plan.id}
                className={`border-border/80 bg-card backdrop-blur-xl relative overflow-hidden flex flex-col justify-between transition-all hover:border-primary/50 shadow-elevation-xs ${
                  plan.isArchived ? 'opacity-60' : ''
                }`}
              >
                {plan.isPopular && (
                  <div className="absolute right-3 top-3 z-10">
                    <Badge variant="success" className="gap-1 font-extrabold text-[10px]">
                      <Sparkles className="size-3" /> Most Popular
                    </Badge>
                  </div>
                )}

                <CardContent className="p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex size-11 items-center justify-center rounded-2xl font-bold ${
                        plan.planType === 'DEALER'
                          ? 'bg-purple-500/10 text-purple-500'
                          : 'bg-primary/10 text-primary'
                      }`}
                    >
                      {plan.planType === 'DEALER' ? <Building2 className="size-6" /> : <Crown className="size-6" />}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-foreground">{plan.name}</h3>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase font-extrabold ${
                          plan.planType === 'DEALER' ? 'border-purple-500/30 text-purple-500' : 'border-primary/30 text-primary'
                        }`}
                      >
                        {plan.planType ?? 'SELLER'} Tier
                      </Badge>
                    </div>
                  </div>

                  <div>
                    <p className="font-display text-3xl font-extrabold text-foreground">
                      ₹{Number(plan.price).toLocaleString()}
                      <span className="text-xs font-normal text-muted-foreground">/{plan.billingCycle?.toLowerCase()}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{plan.description || 'Enterprise subscription plan.'}</p>
                  </div>

                  {/* Quotas Highlight Badges */}
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    <Badge variant="secondary" className="gap-1 bg-muted/60 text-foreground font-semibold">
                      <Zap className="size-3 text-amber-400" /> {activeListings} Active Listings
                    </Badge>
                    <Badge variant="secondary" className="gap-1 bg-muted/60 text-foreground font-semibold">
                      <CheckCircle2 className="size-3 text-emerald-500" /> {leadCreditsVal} Leads
                    </Badge>
                    <Badge variant="secondary" className="gap-1 bg-muted/60 text-foreground font-semibold">
                      <Sparkles className="size-3 text-purple-400" /> {boostCreditsVal} Boosts
                    </Badge>
                  </div>

                  <div className="space-y-2 border-t border-b border-border/60 py-3 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Free Trial Duration:</span>
                      <span className="font-bold text-foreground">{plan.trialDays ?? 0} Days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Grace Period:</span>
                      <span className="font-bold text-foreground">{plan.gracePeriodDays ?? 3} Days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Razorpay Plan Ref:</span>
                      <span className="font-mono text-[11px] font-semibold text-primary truncate max-w-[150px]">
                        {plan.razorpayPlanId || 'Auto-generated'}
                      </span>
                    </div>
                  </div>

                  {/* Actions Row */}
                  <div className="flex items-center justify-between gap-2 pt-2">
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => handleOpenEdit(plan)} className="gap-1 text-xs font-bold">
                        <Edit2 className="size-3.5" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => duplicatePlanMutation.mutate(plan.id)}
                        className="gap-1 text-xs font-bold"
                      >
                        <Copy className="size-3.5" /> Clone
                      </Button>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => archivePlanMutation.mutate(plan.id)}
                        className="gap-1 text-xs text-amber-500 p-1 size-8"
                        title={plan.isArchived ? 'Restore Plan' : 'Archive Plan'}
                      >
                        <Archive className="size-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deletePlanMutation.mutate(plan.id)}
                        className="gap-1 text-xs text-red-500 p-1 size-8"
                        title="Delete Plan"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card className="border-border/80 bg-card p-12 text-center text-xs text-muted-foreground space-y-3">
          <Crown className="mx-auto size-10 opacity-30 text-primary" />
          <p className="font-bold text-sm text-foreground">No plans found for selected filter.</p>
          <p>Click "Create New Plan" to set up a subscription plan.</p>
        </Card>
      )}

      {/* Plan Edit / Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="w-full max-w-2xl my-8 space-y-5 rounded-2xl border border-border/80 bg-card p-6 shadow-elevation-lg">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <h3 className="font-display text-lg font-bold text-foreground">
                {editingPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}
              </h3>
              <Button size="sm" variant="ghost" onClick={() => setIsModalOpen(false)}>
                ✕
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground">Plan Name</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Dealer Pro" />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground">Target Role / Tier</label>
                  <select
                    value={planType}
                    onChange={(e) => setPlanType(e.target.value as any)}
                    className="w-full rounded-lg border border-border/80 bg-background px-3 py-2 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="SELLER">Seller Tier</option>
                    <option value="DEALER">Dealer Enterprise Tier</option>
                    <option value="CUSTOMER">Customer Tier</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground">Description</label>
                <textarea
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-lg border border-border/80 bg-background p-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Plan features summary..."
                />
              </div>

              {/* Pricing Matrix */}
              <div className="grid gap-4 sm:grid-cols-4">
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground">Base Price (₹)</label>
                  <Input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value))} required />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground">Monthly (₹)</label>
                  <Input type="number" value={monthlyPrice} onChange={(e) => setMonthlyPrice(Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground">Quarterly (₹)</label>
                  <Input type="number" value={quarterlyPrice} onChange={(e) => setQuarterlyPrice(Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <label className="font-semibold text-muted-foreground">Yearly (₹)</label>
                  <Input type="number" value={yearlyPrice} onChange={(e) => setYearlyPrice(Number(e.target.value))} />
                </div>
              </div>

              {/* Extended Feature Matrix */}
              <div className="space-y-3 rounded-xl border border-border/80 bg-background/50 p-4">
                <span className="font-bold text-foreground flex items-center gap-1.5">
                  <Settings2 className="size-4 text-primary" /> Feature Quotas & Entitlements
                </span>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Max Active Listings</label>
                    <Input value={maxActiveListings} onChange={(e) => setMaxActiveListings(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Max Images / Listing</label>
                    <Input value={maxImagesPerListing} onChange={(e) => setMaxImagesPerListing(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Max Videos / Listing</label>
                    <Input value={maxVideosPerListing} onChange={(e) => setMaxVideosPerListing(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Max Showroom Branches</label>
                    <Input value={maxBranches} onChange={(e) => setMaxBranches(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Max Staff Accounts</label>
                    <Input value={maxStaffAccounts} onChange={(e) => setMaxStaffAccounts(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Lead Credits Allowance</label>
                    <Input value={leadCredits} onChange={(e) => setLeadCredits(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Razorpay Mapping */}
              <div className="space-y-3 rounded-xl border border-blue-500/30 bg-blue-500/10 p-4">
                <span className="font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1.5 text-xs">
                  Razorpay Gateway Mapping (Optional)
                </span>
                <div className="grid gap-4 sm:grid-cols-2 text-xs">
                  <div className="space-y-1">
                    <label className="font-semibold text-muted-foreground">Razorpay Product ID (Optional)</label>
                    <Input
                      value={razorpayProductId}
                      onChange={(e) => setRazorpayProductId(e.target.value)}
                      placeholder="Optional — e.g. prod_K1x... (Auto-created if empty)"
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="font-semibold text-muted-foreground">Razorpay Plan ID (Optional)</label>
                    <Input
                      value={razorpayPlanId}
                      onChange={(e) => setRazorpayPlanId(e.target.value)}
                      placeholder="Optional — e.g. plan_K1x... (Auto-created if empty)"
                      className="bg-background"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  💡 Leave blank to automatically create/generate test IDs. In production, copy Plan ID from your Razorpay Dashboard or let backend API generate it.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-border/60">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="font-bold bg-primary text-primary-foreground">
                  Save Subscription Plan
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
